/**
 * Sunucudan gelen hatayı kullanıcının anlayacağı cümleye çevirmenin TEK noktası.
 *
 * ── Rota 3.2: mesajın ULAŞMASI ──
 * Backend hatayı iki ayrı anahtarla üretiyordu — Joi doğrulaması `error`, servis
 * katmanı `message`. Ekranların çoğu yalnız birine bakıyor, üçü ise cevaba hiç
 * bakmayıp sabit metin basıyordu; sonuç: kullanıcı "Hata oluştu" görüyordu.
 *
 * ── Rota 3.33: mesajın ANLAŞILMASI ──
 * Ulaşan cümle de kullanıcıya uygun değildi. Ölçüldü: üretilen 189 doğrulama
 * mesajının 154'ü İngilizce, 183'ünde ham kolon adı geçiyordu — 101 karakterlik
 * ünvan girince ekranda `"unvan" length must be less than or equal to 100
 * characters long` çıkıyordu, oysa formda "Ünvan / Pozisyon" yazıyor.
 *
 * Çözüm cümleyi sunucuda çevirmek DEĞİL: sunucu ne olduğunu söyler (alan + kural
 * kodu + parametre), cümleyi arayüz kurar. Sebep iki tane. Etiket formu çizen
 * tarafın bilgisidir — sunucuya kopyalamak rota 2.2'nin "etiket frontend'de"
 * kuralını bozar ve iki kopya ayrışır. Dil ise isteği açan kişinin seçimidir;
 * sunucu bunu bilemez. Bugün tek dil var (çok dil rotada bilerek kapsam dışı) ama
 * bu şekil onu yarın engellemiyor: dil geldiğinde yalnız bu dosya ve etiket
 * kütüğü değişir, sunucuya dokunulmaz.
 *
 * Sunucunun cümlesi YEDEK olarak kalır ve İngilizce kalması bilinçlidir: arayüz
 * dışı çağıran için (INTERNAL_API_KEY yolu) ham alan adı doğru jetondur.
 */

import { formatDateTR } from './timeUtils';

// `limit` sayı DA olabilir metin DE (rota 3.35): uzunluk kurallarında sayı,
// tarih sınırında gün dizgesi (`2000-01-01`) geliyor.
type HataAyrintisi = {
  alan: string;
  kural: string;
  parametre?: { limit?: number | string; degerler?: unknown[] };
};

type SunucuGovdesi = {
  message?: unknown;
  error?: unknown;
  hatalar?: unknown;
};

// Metin isteyen uçlarda (`responseType: 'text'`, proforma önizlemesi) axios hata cevabını da
// ayrıştırmadan verir: sunucunun cümlesi JSON metni içinde kalırdı. JSON olmayan metin gövde sayılmaz.
const govdeAl = (err: unknown): SunucuGovdesi | undefined => {
  const govde = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof govde !== 'string') return govde as SunucuGovdesi | undefined;
  try {
    const ayristirilan: unknown = JSON.parse(govde);
    return ayristirilan && typeof ayristirilan === 'object' ? (ayristirilan as SunucuGovdesi) : undefined;
  } catch (_ayristirmaHatasi) {
    return undefined;
  }
};

const metinMi = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/** Küme listesi uzun olunca bildirim kutusuna sığmıyor (`ilk_kaynak` 13 değer). */
const DEGER_GOSTERIM_SINIRI = 5;

const degerListesi = (degerler: unknown[]): string => {
  const temiz = degerler.filter((v) => v !== null && v !== '').map(String);
  if (temiz.length === 0) return '';
  if (temiz.length > DEGER_GOSTERIM_SINIRI) return `${temiz.slice(0, DEGER_GOSTERIM_SINIRI).join(', ')} ve ${temiz.length - DEGER_GOSTERIM_SINIRI} tane daha`;
  return temiz.join(', ');
};

/**
 * Alan yolundan okunur etiket üretir.
 *
 * Yol noktalı gelir ve dizi indisi içerebilir: `addresses.0.adres_adi`. Yaprak ad
 * kütükte aranır, sayısal parça satır numarasına çevrilir — kullanıcı hangi
 * adres satırının sorunlu olduğunu görsün.
 */
const alanEtiketi = (alan: string, etiketler: Record<string, string>): string | null => {
  const parcalar = alan.split('.');
  const yaprak = parcalar[parcalar.length - 1];
  const etiket = etiketler[yaprak];
  if (!etiket) return null;

  const indis = parcalar.find((p) => /^\d+$/.test(p));
  return indis === undefined ? etiket : `${Number(indis) + 1}. satır — ${etiket}`;
};

/**
 * Sınır değerini kullanıcının okuduğu biçime çevirir (rota 3.35).
 * Beklenen giriş gün dizgesi (`2000-01-01`); tanınmazsa ham hâliyle basılır —
 * eksik bir cümle, hiç cümle olmamasından iyidir.
 */
const sinirTarihi = (limit: number | string | undefined): string => {
  if (typeof limit !== 'string') return String(limit ?? '');
  const bicimli = formatDateTR(limit);
  return bicimli === '-' ? limit : bicimli;
};

/** Kural kodu → Türkçe cümle. Tanınmayan kod `null` döner ve sunucunun cümlesine düşülür. */
const cumle = (d: HataAyrintisi, etiket: string): string | null => {
  const limit = d.parametre?.limit;
  const degerler = d.parametre?.degerler;

  switch (d.kural) {
    case 'string.max':
      return `${etiket} en fazla ${limit} karakter olabilir`;
    case 'string.min':
      return `${etiket} en az ${limit} karakter olmalıdır`;
    case 'any.required':
      return `${etiket} zorunludur`;
    case 'string.empty':
      return `${etiket} boş bırakılamaz`;
    case 'number.base':
      return `${etiket} sayı olmalıdır`;
    case 'number.min':
      return `${etiket} en az ${limit} olmalıdır`;
    case 'number.max':
      return `${etiket} en fazla ${limit} olabilir`;
    case 'boolean.base':
      return `${etiket} evet/hayır değeri olmalıdır`;
    case 'array.base':
      return `${etiket} liste olmalıdır`;
    case 'string.guid':
      return `${etiket} geçerli bir kayıt seçilerek doldurulmalıdır`;
    // Rota 3.35: sipariş kaydetme yolu bu sözlüğe bağlanınca gerekti. O yolda
    // e-posta hatası elle yazılmış bir cümleyle karşılanıyordu ("Email adresini
    // kontrol ediniz") ve elle yazım kaldırıldı; karşılığı burada olmasaydı
    // kullanıcı sunucunun İngilizce cümlesini görürdü. İki alanı birden
    // kapsıyor: siparişin kendi e-postası ve fatura e-postası.
    case 'string.email':
      return `${etiket} geçerli bir e-posta adresi olmalıdır`;
    case 'date.base':
    case 'date.format':
      return `${etiket} geçerli bir tarih olmalıdır`;
    // Tarih sınırları (rota 3.35). Sınır cümleye GG.AA.YYYY olarak girer —
    // sunucu gün dizgesi gönderiyor, kullanıcı Türkçe biçim okuyor.
    case 'date.min':
      return `${etiket} ${sinirTarihi(limit)} tarihinden önce olamaz`;
    case 'date.max':
      return `${etiket} ${sinirTarihi(limit)} tarihinden sonra olamaz`;
    // `alternatives.match` BILEREK burada YOK (rota 3.33 denetimi). Aynı kural kodunu
    // iki farklı şekil üretiyor: tarih alanları (`gunVeyaTamISO`) ve ürün şemasındaki
    // `bundle_icerik` (metin/nesne/dizi). Kod alan adına bakmadan hangisi olduğunu
    // bilemez; tarihe eşlemek ürün formunda "Bundle İçeriği geçerli bir tarih
    // olmalıdır" gibi saçma bir cümle üretirdi. Sunucunun kendi cümlesine düşülür —
    // tarih alanlarında o cümle zaten Türkçe ve daha açıklayıcı ("GG biçiminde ...").
    case 'any.unknown':
      return `${etiket} bu ekrandan gönderilemez`;
    case 'any.only': {
      const liste = degerler ? degerListesi(degerler) : '';
      return liste ? `${etiket} geçersiz — geçerli değerler: ${liste}` : `${etiket} geçersiz bir değer taşıyor`;
    }
    default:
      return null;
  }
};

/** Bildirim kutusuna sığması için gösterilen en fazla hata sayısı. */
const HATA_GOSTERIM_SINIRI = 3;

/**
 * Sunucu hatasını kullanıcıya gösterilecek metne çevirir.
 *
 * Sıra: yapısal ayrıntıdan kurulan cümle → sunucunun kendi cümlesi → yedek.
 * `etiketler` verilmezse ya da alanın karşılığı yoksa sunucunun cümlesine
 * düşülür; yani kütük eksik kalsa bile davranış bugünkünden kötü olmaz.
 */
export const sunucuHataMesaji = (
  err: unknown,
  yedek: string,
  etiketler?: Record<string, string>,
): string => {
  const govde = govdeAl(err);

  if (etiketler && Array.isArray(govde?.hatalar)) {
    const cumleler: string[] = [];
    for (const ham of govde!.hatalar as HataAyrintisi[]) {
      if (!ham || typeof ham.alan !== 'string' || typeof ham.kural !== 'string') continue;
      const etiket = alanEtiketi(ham.alan, etiketler);
      if (!etiket) continue;
      const c = cumle(ham, etiket);
      if (c) cumleler.push(c);
    }
    if (cumleler.length > 0) {
      const gosterilen = cumleler.slice(0, HATA_GOSTERIM_SINIRI).join(' · ');
      const kalan = cumleler.length - HATA_GOSTERIM_SINIRI;
      return kalan > 0 ? `${gosterilen} (+${kalan} tane daha)` : gosterilen;
    }
  }

  if (metinMi(govde?.message)) return govde!.message as string;
  if (metinMi(govde?.error)) return govde!.error as string;
  return yedek;
};
